import { useEffect, useState } from 'react';
import { ArrowRightLeft, TriangleAlert } from 'lucide-react';

import RestaurantUnitConversionService from '../../../services/restaurant/RestaurantUnitConversionService';
import { RestaurantRawMaterialDTO } from '../../../types/restaurant/RestaurantRawMaterialTypes';

interface Props {
  rawMaterial?: RestaurantRawMaterialDTO;
  fromUnitId: number | '';
  quantity: number | '';
}

const nf = new Intl.NumberFormat('es-SV', { maximumFractionDigits: 4 });

const PurchaseLineConversionHint = ({
  rawMaterial,
  fromUnitId,
  quantity,
}: Props) => {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const qty = Number(quantity);
  const baseUnitId = rawMaterial?.unitId;
  const sameUnit = !!fromUnitId && fromUnitId === baseUnitId;

  useEffect(() => {
    setError(false);

    if (!rawMaterial || !fromUnitId || !qty || qty <= 0) {
      setText(null);
      return;
    }

    if (sameUnit) {
      setText(
        `Se compra en la unidad base${
          rawMaterial.unitCode ? ` (${rawMaterial.unitCode})` : ''
        }: no requiere conversión.`
      );
      return;
    }

    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      RestaurantUnitConversionService.convert({
        quantity: qty,
        fromUnitId: Number(fromUnitId),
        toUnitId: Number(baseUnitId),
        rawMaterialId: rawMaterial.id,
      })
        .then((response) => {
          if (cancelled) return;
          const data = response.data;
          setText(
            `${nf.format(qty)} ${data.fromUnitName} ≈ ${nf.format(
              Number(data.convertedQuantity)
            )} ${data.toUnitName} (unidad base) · factor ×${nf.format(
              Number(data.effectiveFactor)
            )}`
          );
          setError(false);
        })
        .catch(() => {
          if (cancelled) return;
          setText('No hay una conversión configurada para esta unidad.');
          setError(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [rawMaterial, fromUnitId, qty, sameUnit, baseUnitId]);

  if (!text) return null;

  return (
    <p
      className={`mt-1 flex items-center gap-1.5 text-xs ${
        error
          ? 'text-warning'
          : 'text-bodydark2 dark:text-bodydark'
      } ${loading ? 'opacity-60' : ''}`}
    >
      {error ? (
        <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
      ) : (
        <ArrowRightLeft className="h-3.5 w-3.5 shrink-0" />
      )}
      {text}
    </p>
  );
};

export default PurchaseLineConversionHint;
